/**
 * Booking domain service � owner console + shop console.
 *
 * Tries the backend first (booking/t/* via apiFetch), falls back to a
 * localStorage mock seeded with demo shops so the consoles work standalone.
 * The backend remains the source of truth for enforcement.
 *
 * Model:
 *  plans (owner-editable) -> shops -> resourceTypes (shop-defined:
 *  Table/Doctor/Class/�) -> resources -> bookings (one calendar per resource)
 */
// ---------- small utils ----------

function pad(n) {
    return (n < 10 ? "0" : "") + n;
}

export function todayKey() {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function toMin(t) {
    const [h, m] = String(t || "0:00").split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
}

export function fmtTime(t) {
    const [hRaw, m] = String(t).split(":");
    const h = Number(hRaw);
    const ap = h >= 12 ? "PM" : "AM";
    const hh = h % 12 === 0 ? 12 : h % 12;
    return `${hh}:${m} ${ap}`;
}

export function addDays(key, n) {
    const [y, m, d] = String(key || todayKey()).split("-").map(Number);
    const dt = new Date(y || 2000, (m || 1) - 1, d || 1);
    dt.setDate(dt.getDate() + (n || 0));
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

export function parseKey(key) {
    const [y, m, d] = String(key || todayKey()).split("-").map(Number);
    return new Date(y || 2000, (m || 1) - 1, d || 1);
}

export function fmtLong(key) {
    return parseKey(key).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

export function fmtShort(key) {
    return parseKey(key).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Scheduling config for a resource type, with sane defaults. */
export function slotCfg(type) {
    return {
        slotMin: type?.slotMin || 30,
        dayStart: type?.dayStart ?? 8,
        dayEnd: type?.dayEnd ?? 22,
        leadMin: type?.leadMin ?? 15,
        advanceDays: type?.advanceDays ?? 30,
    };
}

export function fmtHour(h) {
    const hh = ((h % 24) + 24) % 24;
    const ap = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:00 ${ap}`;
}

export function isActiveBooking(b) {
    return b.status !== "cancelled" && b.status !== "noshow";
}

export function overlaps(existing, resourceId, date, start, end, ignoreId) {
    const s = toMin(start);
    const e = toMin(end);
    return (existing || []).some((b) => {
        if (b.id === ignoreId || b.resourceId !== resourceId || b.date !== date || !isActiveBooking(b)) return false;
        return s < toMin(b.end) && toMin(b.start) < e;
    });
}

/** Free/busy slots for one resource on one day. */
export function getSlots(bookings, resourceId, date, { slotMin = 60, dayStart = 8, dayEnd = 22, leadMin = 15, advanceDays = 30 } = {}) {
    const tk = todayKey();
    if (!date || date < tk || date > addDays(tk, advanceDays ?? 30)) return [];
    const startMin = dayStart * 60;
    const endMin = dayEnd * 60;
    const now = new Date();
    const nowM = now.getHours() * 60 + now.getMinutes();
    const busy = (bookings || [])
        .filter((b) => b.resourceId === resourceId && b.date === date && isActiveBooking(b))
        .map((b) => [toMin(b.start), toMin(b.end)]);
    const fmt = (mm) => `${pad(Math.floor(mm / 60))}:${pad(mm % 60)}`;
    let m0 = startMin;
    if (date === tk) m0 = Math.max(startMin, Math.ceil((nowM + leadMin) / 5) * 5);
    const out = [];
    for (let m = m0; m + slotMin <= endMin; m += slotMin) {
        let taken = busy.some(([bs, be]) => m < be && bs < m + slotMin);
        if (date < tk) taken = true;
        out.push({ start: fmt(m), end: fmt(m + slotMin), available: !taken });
    }
    return out;
}


const BOOKING_STATUSES = ["pending", "confirmed", "completed", "cancelled", "noshow"];


export { BOOKING_STATUSES };
